/**
 * `people` → **Customize People column** (spec M2 tier-1 §5.7, card P-123).
 *
 * The capture reaches the row — `editors/people/settings-dialog.json` is the `Settings` submenu
 * and its first entry is *Customize People column* — and stops there; the dialog was never
 * opened. Its one documented setting is `max_people_allowed`, a **string** in which `"0"` means
 * unlimited `[09§A.7 people]`, which is exactly the "allow multiple / max people allowed" pair
 * card P-123 names.
 *
 * The two controls therefore write one key: the switch sets `"0"` (unlimited) or `"1"`, and the
 * number replaces it. The string spelling is monday's, not ours — writing the number `1` here
 * would change `settings_str`'s shape and break a round-trip through `/v2`.
 */
import type { PeopleSettings } from '@elizmonday/shared';
import {
  HideSummaryField,
  SettingsCheckbox,
  SettingsField,
  SettingsForm,
  useSettingsDraft,
} from '../settings-kit';
import type { ColumnSettingsProps } from '../types';

/** `"0"`, `0`, `undefined` and `""` all mean "no cap" `[09§A.7 people]`. */
export function peopleLimit(settings: PeopleSettings): number {
  const raw = settings.max_people_allowed;
  const parsed = typeof raw === 'number' ? raw : Number(String(raw ?? '0').trim() || '0');
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : 0;
}

export function PeopleSettingsDialog({ settings, onCommit, onCancel }: ColumnSettingsProps<PeopleSettings>) {
  const [draft, patch] = useSettingsDraft<PeopleSettings>(settings);
  const limit = peopleLimit(draft);

  return (
    <SettingsForm
      title="People column settings"
      testId="people-settings"
      onSave={() => onCommit(draft)}
      onCancel={onCancel}
    >
      <SettingsCheckbox
        label="Limit how many people one cell may hold"
        testId="people-limit"
        checked={limit > 0}
        onChange={(next) => patch({ max_people_allowed: next ? '1' : '0' })}
      />
      {limit > 0 ? (
        <SettingsField
          label="Max people allowed"
          testId="people-max"
          type="number"
          inline
          min={1}
          max={99}
          value={String(limit)}
          onChange={(next) => {
            const parsed = Number(next);
            patch({ max_people_allowed: String(Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : 1) });
          }}
        />
      ) : null}
      <HideSummaryField
        testId="people-hide-footer"
        checked={draft.hide_footer ?? false}
        onChange={(next) => patch({ hide_footer: next })}
      />
    </SettingsForm>
  );
}
